<template>
  <div
    :id="`section-editor-${section.id}`"
    class="blum-report-editor"
    :class="{ 'section-focused': isFocused }"
  >
    <div class="report-editor-close" :class="{ 'section-focused': isFocused }">
      <div class="w-6 h-6 cursor-pointer" @click="onClose">
        <BlIcon icon="close" class="w-6 h-6" clickable />
      </div>
    </div>
    <vue-editor
      :editor-toolbar="customToolbar"
      v-model="htmlInput"
      :placeholder="mode === 'edit' ? 'Type your text here' : ''"
    />
    <div class="report-editor-delete" :class="{ 'section-focused': isFocused }">
      <BlInputButton
        type="outlined-error"
        dense
        label="Delete"
        :loading="removeLoading"
        @clicked="onRemoveSection"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { VueEditor } from "vue2-editor";

import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "ReportTextEditor",
  components: { VueEditor, BlIcon, BlInputButton },
  props: {
    section: { type: Object },
    mode: { type: String }, // edit | preview
    isFocused: { type: Boolean }
    // @change-saving
    // @change-focus-id
  },
  data() {
    return {
      inputTimeout: null,
      htmlInput: "",
      customToolbar: [
        [{ header: [false, 1, 2, 3] }],
        ["bold", "italic", "underline"],
        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" }
        ],
        [{ list: "bullet" }, { list: "ordered" }],
        ["link"],
        ["clean"]
      ],
      removeLoading: false
    };
  },
  computed: {
    ...mapGetters([""])
  },
  watch: {
    section: {
      deep: true,
      handler(val) {
        this.htmlInput = val.data;
      }
    },
    htmlInput(val) {
      if (this.mode !== "edit") return;
      const init = this.section.data;
      if (val !== init) {
        clearTimeout(this.inputTimeout);
        this.$emit("change-saving", true);
        this.inputTimeout = setTimeout(this.onUpdateText, 1500);
      }
    }
  },
  mounted() {
    this.htmlInput = this.section.data;
  },
  methods: {
    ...mapActions(["updateSection", "deleteSection"]),
    onClose() {
      setTimeout(() => {
        this.$emit("change-focus-id", 0);
      }, 10);
    },
    //
    async onUpdateText() {
      /**
       kind
       * text
       * heading1
       * heading2
       * heading3
      */
      let kind = "text";
      if (this.htmlInput.includes("</h3>")) kind = "heading3";
      if (this.htmlInput.includes("</h2>")) kind = "heading2";
      if (this.htmlInput.includes("</h1>")) kind = "heading1";
      const data = {
        id: this.section.id,
        section: { data: this.htmlInput, kind }
      };
      await this.updateSection(data);
      this.$emit("change-saving", false);
    },
    async onRemoveSection() {
      if (this.mode !== "edit") return;
      this.removeLoading = true;
      await this.deleteSection({ id: this.section.id });
      this.removeLoading = false;
    }
  }
};
</script>

<style lang="scss">
.blum-report-editor {
  font-family: "Roboto", sans-serif;
  position: relative;

  .ql-toolbar {
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    z-index: 1;
    margin-top: 1rem;
    padding: 0 5rem 0 2.5rem !important;
    display: none;
    align-items: center;
    gap: 1rem;
    height: 3rem;
    border-color: var(--Grey-2, #e4e4e4) !important;
    border-radius: 5px;
    background-color: white;
    .ql-formats {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0 !important;
      &::after {
        content: "";
        position: absolute;
        right: -0.5625rem;
        height: 100%;
        width: 0.1875rem;
        flex-shrink: 0;
        background-color: var(--Slate-3, #f2f4f7) !important;
      }

      .ql-picker {
        font-family: "Roboto", sans-serif;
        height: 1.875rem !important;
        width: 8.5rem !important;
        position: relative;
      }

      .ql-picker-label {
        bottom: 0.125rem;
        display: flex !important;
        align-items: center;
        padding: 0 0.75rem !important;
        font-size: 14px !important;
        border-radius: 5px;
        border: 1px solid var(--Slate-4, #eaecf0) !important;
        font-weight: 400;
        color: var(--Slate-8, #475467) !important;
        svg {
          right: 0.5rem !important;
        }
      }

      button.ql-active {
        color: unset !important;
        background-color: var(--Slate-3, #f2f4f7) !important;
      }
      button {
        padding: 0 !important;
        height: 1.875rem !important;
        width: 1.875rem !important;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        svg {
          width: 1rem !important;
        }
      }
    }
  }
  &.section-focused {
    .ql-toolbar {
      display: flex;
    }
  }
  .ql-container {
    background-color: white;
    border: none !important;
    .ql-editor {
      padding: 0 !important;
      min-height: unset !important;
      max-height: unset !important;
      //
      color: var(--Slate-10, #1d2939);
      font-family: "Roboto", sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      letter-spacing: 0.5px;
      text-align: left;
      h1 {
        font-size: 20px;
        font-weight: 700;
        line-height: 28px;
      }
      h2 {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
      }
      h3 {
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
      }
    }
    .ql-tooltip {
      z-index: 2;
    }
  }

  .report-editor-delete {
    display: none;
    position: absolute;
    left: calc(50% + 19.25rem);
    top: 100%;
    transform: translateX(-50%);
    z-index: 2;
    margin-top: 1rem;
    align-items: center;
    justify-content: center;
    height: 3rem;
  }
  &.section-focused {
    .report-editor-delete {
      display: flex;
    }
  }

  .report-editor-close {
    display: none;
    position: absolute;
    right: calc(50% + 19.25rem);
    top: 100%;
    transform: translateX(-50%);
    z-index: 2;
    margin-top: 1rem;
    align-items: center;
    justify-content: center;
    height: 3rem;
  }
  &.section-focused {
    .report-editor-close {
      display: flex;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/reports/editor/TextEditor.vue